import type {
  DateFieldProps,
  DateInputProps as DateInputPropsRac,
  DateSegmentProps,
  DateValue as DateValueRac,
  TimeFieldProps,
  TimeValue as TimeValueRac,
} from 'react-aria-components';
import {
  composeRenderProps,
  DateField as DateFieldRac,
  DateInput as DateInputRac,
  DateSegment as DateSegmentRac,
  I18nProvider,
  TimeField as TimeFieldRac,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils';

type GMNDateFieldProps<T extends DateValueRac> = DateFieldProps<T> & { locale?: string };

function DateField<T extends DateValueRac>({
  className,
  children,
  locale,
  ...props
}: GMNDateFieldProps<T>) {
  const { i18n } = useTranslation();
  const resolvedLocale = locale ?? i18n.language ?? 'en-GB';

  return (
    <I18nProvider locale={resolvedLocale}>
      <DateFieldRac
        className={composeRenderProps(className, (className) => cn(className))}
        {...props}
      >
        {children}
      </DateFieldRac>
    </I18nProvider>
  );
}

function TimeField<T extends TimeValueRac>({ className, children, ...props }: TimeFieldProps<T>) {
  return (
    <TimeFieldRac
      className={composeRenderProps(className, (className) => cn(className))}
      {...props}
    >
      {children}
    </TimeFieldRac>
  );
}

function DateSegment({ className, ...props }: DateSegmentProps) {
  return (
    <DateSegmentRac
      className={composeRenderProps(className, (className) =>
        cn(
          `
            text-foreground outline-hidden
            data-focused:bg-accent
            data-invalid:data-focused:bg-destructive
            data-focused:data-placeholder:text-foreground data-focused:text-foreground
            data-invalid:data-placeholder:text-destructive data-invalid:text-destructive
            data-placeholder:text-muted-foreground/70
            data-[type=literal]:text-muted-foreground/70
            inline rounded p-0.5 caret-transparent
            data-disabled:cursor-not-allowed data-disabled:opacity-50
            data-invalid:data-focused:text-white data-invalid:data-focused:data-placeholder:text-white
            data-[type=literal]:px-0
          `,
          className
        )
      )}
      {...props}
      data-invalid
    />
  );
}

const dateInputStyle =
  'relative inline-flex h-9 w-full items-center overflow-hidden whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 text-sm shadow-2xs transition-[color,box-shadow] outline-hidden data-focus-within:border-ring data-focus-within:ring-ring/50 data-focus-within:ring-[3px] data-focus-within:has-[aria-invalid]:ring-destructive/20 dark:data-focus-within:has-[aria-invalid]:ring-destructive/40 data-focus-within:has-[aria-invalid]:border-destructive';

interface DateInputProps extends DateInputPropsRac {
  className?: string;
  unstyled?: boolean;
}

function DateInput({ className, unstyled = false, ...props }: Omit<DateInputProps, 'children'>) {
  return (
    <DateInputRac
      className={composeRenderProps(className, (className) =>
        cn(!unstyled && dateInputStyle, className)
      )}
      {...props}
    >
      {(segment) => <DateSegment segment={segment} />}
    </DateInputRac>
  );
}

export { DateField, DateInput, DateSegment, TimeField, dateInputStyle };
export type { DateInputProps };
